<template>
    <div>
        <b-alert v-model="isLocked" variant="info" dismissible>
            {{ this.lockMessage }}
        </b-alert>
        <b-row>
            <b-col cols="6">
                <!-- Show Document -->
                <ImageComponent style="height:835px; border: 1px solid black;" :imageID="imageID">
                </ImageComponent>
                <b-button v-if="!!imageID" @click="resendFax" class="mt-2">
                    Resend Fax
                </b-button>
            </b-col>
            <b-col cols="6">
                <h3>{{ value.requestName }}</h3>
                <!-- Doctor information -->
                <div v-if="!!value.storeID">
                    <router-link target="_blank" class="ml-2" v-if="prescription" :to="prescriptionDetailsRoute">
                        <span>Go To Rx...</span>
                    </router-link>
                </div>
                <hr>
                <div>
                    <b>TO DOCTOR: </b><span>{{ doctor.displayNamesForPerson() }}</span><br>
                    <span>{{ doctor.fullAddress }}</span><br>
                    <b>PHONE: </b><span>{{ doctor.phoneNumber }}</span><br>
                    <b>FAX: </b><span>{{ doctor.faxNumber }}</span>
                    <b-check v-if="doctor.altFaxNumber" v-model="useAltFax">
                        <b>ALT FAX: </b> <span>{{ doctor.altFaxNumber }}</span>
                    </b-check>
                </div>
                <hr>

                <!-- Patient Information -->
                <div>
                    <b>PATIENT: </b><span>{{ patient.displayNamesForPerson() }}</span><br>
                    <span>{{ patient.fullAddress }}</span><br>
                    <b>PHONE: </b><span>{{ patient.phoneNumber }}</span><br>
                    <b>PATIENT DOB: </b><span>{{ patient.dateOfBirth | formatDate('L', false) }}</span>
                </div>
                <hr>

                <!-- Rx Information -->
                <PrescriptionDetailsSummary v-if="prescription && value.type != requestTypeEnum.NewRxRequest"
                    v-model="prescription"></PrescriptionDetailsSummary>
                <div v-if="drug">
                    <b>PRESCRIBED: </b><span>{{ drug.productNameLong?.length > 0 ? drug.productNameLong :
                        drug.productNameShort }}</span><br>
                    <b>NDC: </b><span>{{ drug.ndc }}</span>
                </div>
                <hr>

                <!-- Notes -->
                <div>
                    <b>Notes: </b>
                    <NotesList ref="notes-list" :paging="false" :entityId="+value.id" :entityType="entityType" />
                </div>

                <div class="form-group" v-if="!isLocked">
                    <NotesForm :entityId="+value.id" :entityType="entityType" @saved="refreshNotesList"
                        :templates="noteTemplates" />
                </div>
            </b-col>
        </b-row>

        <!-- Action Buttons-->
        <b-row v-if="!isLocked && !isSending">
            <b-col>
                <b-button variant="warning" @click="confirmDialog(prescriberFollowUpStatusEnum.InProgress)"
                    v-b-tooltip.hover title="Click if no response." :disabled='disableHold'>Hold</b-button>
                <b-button variant="danger" @click="confirmDialog(prescriberFollowUpStatusEnum.NotResponded)"
                    v-b-tooltip.hover
                    title="Click to close case if it won't be possible to contact doctor or the doctor denies the request.">
                    No Success
                </b-button>
                <b-button variant="success" @click="confirmDialog(prescriberFollowUpStatusEnum.Responded)"
                    v-b-tooltip.hover
                    title="Click if doctor responded and all information was gathered.">Complete</b-button>
            </b-col>
        </b-row>

        <b-row v-if="isSending">
            <b-spinner label="Spinning" class="mr-2" />
            <strong>Sending...</strong>
        </b-row>

        <PatientRxHistoryModalComponent ref="doc_form_patient_rx_history_modal" :patient="patient" />

        <patient-notes-modal-component v-if="patient && patient.id" :entity-id="patient.id" />
        <doctor-notes-modal-component v-if="doctor && doctor.id" :entity-id="doctor.id" />
        <drug-notes-modal-component v-if="drug && drug.drugId" :entity-id="drug.drugId" :drug-source="drug.source" />
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { LockHandler, Lock } from '@/mixins/LockHandler';
import moment from "moment";

import { PrescriberFollowUpCase, FaxType, PrescriberFollowUpStatus } from '@/models/PrescriberFollowUpCase';
import { Prescriber } from '@/models/Prescriber';
import { Patient } from '@/models/Patient';
import { PatientDocument } from '@/models/PatientDocument';
import { Prescription } from '@/models/Prescription';
import { Drug } from '@/models/Drug/Drug';
import { Note } from '@/models/Note';

import ImageComponent from '@/components/Prescription/ImageComponent.vue';
import PrescriptionDetailsSummary from '@/components/Prescription/PrescriptionDetailsSummary.vue';
import NotesList from '@/components/Note/NotesList.vue';
import NotesForm from '@/components/Note/NotesForm.vue';
import PatientRxHistoryModalComponent from '@/components/Patient/PatientRxHistoryModalComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { EntityType } from "@/models/EntityType";
import DoctorNotesModalComponent from "@/components/Note/DoctorNotesModalComponent.vue";
import PatientNotesModalComponent from "@/components/Note/PatientNotesModalComponent.vue";
import DrugNotesModalComponent from "@/components/Note/DrugNotesModalComponent.vue";

@Component({
    name: "DoctorFollowUpCaseForm",
    components: {
        DrugNotesModalComponent,
        PatientNotesModalComponent,
        DoctorNotesModalComponent,
        ImageComponent,
        PrescriptionDetailsSummary,
        NotesList, NotesForm,
        PatientRxHistoryModalComponent,
    },
})
export default class DoctorFollowUpCaseForm extends Mixins(LockHandler) {
    @Prop() private value!: PrescriberFollowUpCase;
    private lock: Lock | null = null;
    private isLocked = false;
    private lockMessage = '';
    private isSending = false;
    private maxDays: number = 7;

    private doctor: Prescriber = new Prescriber();
    private patient: Patient = new Patient();
    private followUpCase: PrescriberFollowUpCase = new PrescriberFollowUpCase();

    private prescription: Prescription | null = null;
    private drug: Drug | null = null;

    private imageID: number = 0;
    private entityType = EntityType.PrescriberFollowUpCase;

    protected notes: Array<Note> = Array<Note>();
    protected prescriberFollowUpStatusEnum = PrescriberFollowUpStatus;
    protected requestTypeEnum = FaxType;

    protected useAltFax = false;

    loadInfo() {
        if (!this.value.id) return;

        this.isLocked = false;
        this.addLockWithURL(`/prescriber/follow-up-case/${this.value.id}/lock`, 60000)
            .then(res => this.lock = res)
            .catch(err => {
                console.log(`Added lock -- error == ${err}`);
                if (err.response && err.response.status == 418) {
                    // patient locked
                    this.showLockedMessage(
                        EntityType.PrescriberFollowUpCase,
                        this.value?.id.toString(),
                        this.value?.id.toString(),
                        err.response.data
                    );
                }
            })
            .finally(() => this.fetchData());
    }

    created() {
        this.loadInfo();
    }

    @Watch('value')
    onValueChange(newValue: PrescriberFollowUpCase, oldValue: PrescriberFollowUpCase) {
        if (newValue.id && oldValue.id && newValue.id === oldValue.id) return;
        this.loadInfo();
    }

    async fetchData() {
        this.followUpCase = new PrescriberFollowUpCase(this.value);

        let resDoctor = await this.$http.get<Prescriber>(`/Prescriber/${this.value.prescriberId}`)
            .catch(err => this.catchError(err, "Error loading doctor information."));

        if (resDoctor?.data.id) this.doctor = new Prescriber((resDoctor as AxiosResponse).data);

        let resPatient = await this.$http.get<Patient>(`/Patient/${this.value.patientId}`)
            .catch(err => this.catchError(err, "Error loading patient information."));

        if (resPatient?.data.id) this.patient = new Patient(resPatient.data);

        let resDocs = await this.$http.get<Array<PatientDocument>>(`/Patient/${this.value.patientId}/get-documents`)
            .catch(err => this.catchError(err, "Error loading patient documents."));

        if (resDocs?.data.length)
            this.imageID = resDocs.data.find((doc: PatientDocument) => doc.id == this.value.documentId)?.imageId ?? 0;

        if (this.value.packageID) {
            let resDrug = await this.$http.get<Drug>(`/drug/${this.value.packageID}`)
                .catch(err => this.catchError(err, "Error loading drug information."));

            if (resDrug?.data?.packageID)
                this.drug = new Drug(null, resDrug.data);
        }

        if (this.value.rxNumber) {
            const rxID = Prescription.createRxId(this.value.storeID, this.value.rxNumber, this.value.rfNumber);
            let resRx = await this.$http.get<Prescription>(`/prescription/${rxID}`)
                .catch(err => this.catchError(err, "Error loading prescription information."));

            if (resRx?.data.rxNumber)
                this.prescription = new Prescription(resRx.data);
        }
    }

    catchError(err: any, errorMsg: string): AxiosResponse {
        console.error(errorMsg, { err, response: err?.response });
        this.$notification(NotificationOptions.error(err));
        return {} as AxiosResponse;
    }

    get prescriptionDetailsRoute() {
        return {
            name: 'PrescriptionDetails'
            , params: {
                storeID: this.prescription?.storeID,
                rxNumber: this.prescription?.rxNumber,
                rfNumber: (this.prescription?.rfNumber || 0),
            },
        };
    }

    submit(statusToSubmit: PrescriberFollowUpStatus) {
        this.followUpCase.status = statusToSubmit;

        this.isSending = true;
        this.$http.post<PrescriberFollowUpCase>('/Prescriber/follow-up-queue', this.followUpCase, { params: { useAltFax: this.useAltFax } })
            .then(res => {
                if (res?.data.id) {
                    this.followUpCase = new PrescriberFollowUpCase(res.data);
                    this.$notification(NotificationOptions.notificationOptionsPreset(`Follow-up passed to status: ${this.followUpCase.statusName}`, NotificationOptions.NotificationTypes.success));
                    this.$emit('submitted', this.followUpCase);
                } else this.$notification(NotificationOptions.errorSaveNotificationPreset(`Follow-up case`));
            })
            .catch(err => {
                this.catchError(err, "Error submitting follow up case");
                this.$notification(NotificationOptions.errorSaveNotificationPreset(`Follow-up case`, err as AxiosError));
            })
            .finally(() => this.isSending = false);
    }

    get disableHold(): boolean {
        return moment().subtract(this.maxDays - 1, 'day').isSameOrAfter(moment(this.value.created), 'day');
    }

    confirmDialog(status: PrescriberFollowUpStatus) {
        let message = '';
        if (status == PrescriberFollowUpStatus.InProgress)
            message = "The case will be hidden for 48hrs from the queue to try again another day";
        if (status == PrescriberFollowUpStatus.NotResponded)
            message = "The case will be closed because the doctor won't be able to be contacted or the request was denied";
        if (status == PrescriberFollowUpStatus.Responded)
            message = "The case will be closed because all the information needed was gathered";
        this.$bvModal.msgBoxConfirm(`${message}. Are you sure?`, {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'info',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
        })
            .then(value => {
                if (!value) return;
                this.submit(status);
            })
            .catch(err => {
                console.error("Error caught on Clear button.", err);
            });
    }

    mounted() {
        document.addEventListener("keydown", this.preventDefaultForHotkeys);
        document.addEventListener("keyup", this.hotKeysListener);
        this.fetchNoteTemplates();
        this.fetchMaxDays();
    }

    beforeDestroy() {
        document.removeEventListener("keydown", this.preventDefaultForHotkeys);
        document.removeEventListener("keyup", this.hotKeysListener);
    }

    preventDefaultForHotkeys(e: any) {
        if (e.code === "F5") e.preventDefault();
    }

    hotKeysListener(e: any) {
        if (e.code === 'F5' && this.patient?.id) {
            this.openRxHistory();
        }
    }

    openRxHistory() {
        const modal = this.$refs["doc_form_patient_rx_history_modal"] as PatientRxHistoryModalComponent;
        if (modal) modal?.openRxHistory();
        else console.error("Reference dont exist for the component", modal);
    }

    async resendFax() {
        try {
            await axios.post(`/api/Prescriber/follow-up-case/${this.followUpCase.id}/resend-fax`, null, { params: { useAltFax: this.useAltFax } });
            this.$notification(NotificationOptions.notificationOptionsPreset('Fax resent successfully', NotificationOptions.NotificationTypes.success));
            this.refreshNotesList();
        } catch (e) {
            this.$notification(NotificationOptions.error(e));
        }
    }

    @Ref("notes-list") protected notesList!: NotesList;

    refreshNotesList() {
        this.notesList.loadNotes();
    }

    protected noteTemplates: string[] = [];

    async fetchNoteTemplates() {
        try {
            const resp = await axios.get<string[]>("/api/Configuration/PrescriberFollowUpNoteTemplates");
            this.noteTemplates = resp.data;
        } catch (err) {
            console.error(err);
        }
    }

    async fetchMaxDays() {
        try {
            const resp = await axios.get<number>("/api/Configuration/DoctorFollowUpMaxDays");
            this.maxDays = resp.data ?? 7;
        } catch (err) {
            console.error(err);
        }
    }
}

</script>

<style scoped></style>
